import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiHome, FiBook, FiLayers, FiFileText, FiPlusCircle } from 'react-icons/fi';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { showToast } from '../../components/common/Toaster';
import { publisherGradeAPI, publisherSubjectAPI, publisherPublicationAPI } from '../../api/publisherEndpoints';
import { formatDate } from '../../utils/helpers';
import {
  DashboardContainer,
  DashboardHeader,
  StatsGrid,
  StatCard,
  StatIcon,
  StatInfo,
  SectionTitle,
  Card,
  CardHeader,
  CardBody,
  ContentGrid,
  ContentCard,
  ContentCardHeader,
  ContentCardBody,
  ContentCardFooter,
  ContentActions,
  ActionButton,
} from './Dashboard.styles';

const PublisherDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    grades: 0,
    subjects: 0,
    publications: 0,
  });
  const [recentPublications, setRecentPublications] = useState([]);

  const menuItems = [
    { icon: <FiHome />, label: 'Dashboard', path: '/publisher' },
    { icon: <FiFileText />, label: 'Publications', path: '/publisher/publications' },
    { icon: <FiLayers />, label: 'Grades', path: '/publisher/grades' },
    { icon: <FiBook />, label: 'Subjects', path: '/publisher/subjects' },
  ];

  useEffect(() => {
    fetchOverview();
  }, []);

  const fetchOverview = async () => {
    try {
      setLoading(true);
      const [gradesRes, subjectsRes, publicationsRes, recentRes] = await Promise.all([
        publisherGradeAPI.getGrades({ page: 1, per_page: 1 }),
        publisherSubjectAPI.getSubjects({ page: 1, per_page: 1 }),
        publisherPublicationAPI.getPublications({ page: 1, per_page: 1 }),
        publisherPublicationAPI.getPublications({ page: 1, per_page: 3, sort_by: 'created_at', sort_order: 'desc' }),
      ]);

      const gradesTotal = gradesRes.data?.pagination?.total ?? (gradesRes.data?.grades?.length || 0);
      const subjectsTotal = subjectsRes.data?.pagination?.total ?? (subjectsRes.data?.subjects?.length || 0);
      const publicationsTotal = publicationsRes.data?.pagination?.total ?? (publicationsRes.data?.publications?.length || 0);

      setStats({
        grades: gradesTotal,
        subjects: subjectsTotal,
        publications: publicationsTotal,
      });
      setRecentPublications(recentRes.data?.publications || []);
    } catch (error) {
      showToast('error', 'Failed to load publisher overview');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout menuItems={menuItems}>
      <DashboardContainer>
        <DashboardHeader>
          <div>
            <h1>Publisher Portal</h1>
            <p>Manage grades, subjects, and publications from one place</p>
          </div>
          <Button leftIcon={<FiPlusCircle />} onClick={() => navigate('/publisher/publications')}>
            New Publication
          </Button>
        </DashboardHeader>

        {loading ? (
          <LoadingSpinner size="large" text="Loading publisher dashboard..." />
        ) : (
          <>
            <StatsGrid>
              <StatCard>
                <StatIcon color="#2c5f34">
                  <FiFileText />
                </StatIcon>
                <StatInfo>
                  <h3>{stats.publications}</h3>
                  <p>Total Publications</p>
                </StatInfo>
              </StatCard>
              <StatCard>
                <StatIcon color="#10B981">
                  <FiLayers />
                </StatIcon>
                <StatInfo>
                  <h3>{stats.grades}</h3>
                  <p>Active Grades</p>
                </StatInfo>
              </StatCard>
              <StatCard>
                <StatIcon color="#F59E0B">
                  <FiBook />
                </StatIcon>
                <StatInfo>
                  <h3>{stats.subjects}</h3>
                  <p>Subjects Catalog</p>
                </StatInfo>
              </StatCard>
            </StatsGrid>

            <SectionTitle>Quick Actions</SectionTitle>
            <ContentGrid>
              <Card>
                <CardHeader>
                  <h3>Create a Publication</h3>
                </CardHeader>
                <CardBody>
                  Upload a PDF, assign it to a grade and subject, then publish.
                </CardBody>
                <div style={{ marginTop: '16px' }}>
                  <Button onClick={() => navigate('/publisher/publications')}>
                    Go to Publications
                  </Button>
                </div>
              </Card>
              <Card>
                <CardHeader>
                  <h3>Maintain Grades</h3>
                </CardHeader>
                <CardBody>
                  Keep curriculum grade names clean and aligned to your catalog.
                </CardBody>
                <div style={{ marginTop: '16px' }}>
                  <Button variant="ghost" onClick={() => navigate('/publisher/grades')}>
                    Manage Grades
                  </Button>
                </div>
              </Card>
              <Card>
                <CardHeader>
                  <h3>Update Subjects</h3>
                </CardHeader>
                <CardBody>
                  Review subject codes and descriptions for every publication.
                </CardBody>
                <div style={{ marginTop: '16px' }}>
                  <Button variant="ghost" onClick={() => navigate('/publisher/subjects')}>
                    Manage Subjects
                  </Button>
                </div>
              </Card>
            </ContentGrid>

            <SectionTitle style={{ marginTop: '32px' }}>Recent Publications</SectionTitle>
            {recentPublications.length === 0 ? (
              <Card>
                <CardBody>No publications yet. Upload your first PDF to get started.</CardBody>
              </Card>
            ) : (
              <ContentGrid>
                {recentPublications.map((publication) => (
                  <ContentCard key={publication.id}>
                    <ContentCardHeader>
                      <div>
                        <h3>{publication.title}</h3>
                        <span className="badge" style={{ background: '#2c5f3420', color: '#2c5f34' }}>
                          PDF
                        </span>
                      </div>
                    </ContentCardHeader>
                    <ContentCardBody>
                      <p>{publication.subject?.name || 'Subject'} / {publication.grade?.name || 'Grade'}</p>
                      <div className="meta">
                        <span>Created {formatDate(publication.created_at)}</span>
                        <span>{publication.file_type?.toUpperCase() || 'PDF'}</span>
                      </div>
                    </ContentCardBody>
                    <ContentCardFooter>
                      <ContentActions>
                        <ActionButton
                          title="Open PDF"
                          onClick={() => publication.pdf_url && window.open(publication.pdf_url, '_blank')}
                        >
                          <FiFileText />
                        </ActionButton>
                      </ContentActions>
                    </ContentCardFooter>
                  </ContentCard>
                ))}
              </ContentGrid>
            )}
          </>
        )}
      </DashboardContainer>
    </DashboardLayout>
  );
};

export default PublisherDashboard;
